import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  Stack,
  Container,
  useMediaQuery,
  useTheme,
  Typography,
} from "@mui/material";
import SmartButton from "./SmartButton";
import checkCircleImage from "../../assets/check_icon.png";
import VideoPlayer from "./VideoPlayer";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const HeroSection = ({
  titleContent, // HTML string (desktop)
  subtitleContent, // HTML string
  showbuttonsfirst = false,
  buttons = [], // [{ label, icon, onClick }]
  buttonsDesc = "",
  points = [],
  heroImage,
  heroImageType = "image"
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const leftRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (leftRef.current) {
      setContentHeight(leftRef.current.offsetHeight);
    }
  }, [titleContent, subtitleContent, points, buttons]);


  const navigate = useNavigate();

  // Animation Variants
  const fadeLeft = {
    hidden: { opacity: 0, x: -40 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const fadeRight = {
    hidden: { opacity: 0, x: 40 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  // Auto-generate a simpler mobile version if screen is small

  const getResponsiveTitle = (html) => {
    if (!html) return "";

    let clean = html
      .replace(/style="[^"]*"/g, "") // ✅ REMOVE inline styles
      .replace(/font-size:\s*\d+px/gi, "") // extra safety
      .replace(/<br\s*\/?>/gi, "")
      .replace(/&nbsp;/gi, "")
      .replace(/<\/?i>/g, "")
      .replace(/<\/?em>/g, "");

    return clean;
  };

  const getResponsiveHtml = (html) => {
    if (!html) return "";
    if (!isMobile) return html.replace(/<br\s*\/?>/gi, "").replace(/&nbsp;/gi, " ").replace(/\u00A0/gi, " ").replace(/\[empty_line\]/gi, "<br />");
    return html
      .replace(/<br\s*\/?>/gi, "")
      .replace(/font-size:\s*\d+px/gi, "font-size: 18px")
      .replace(/font-weight:\s*900/gi, "font-weight: 700").replace(/&nbsp;/gi, " ").replace(/\u00A0/gi, " ").replace(/\[empty_line\]/gi, "<br />");
  };

  const handleClick = (rawLink) => {
    if (!rawLink) return;

    const link = rawLink.trim();

    // ✅ Book Free Guidance (hash route)
    if (link.startsWith("#")) {
      navigate(link);
      return;
    }

    // ✅ Internal route
    if (link.startsWith("/")) {
      navigate(link);
      return;
    }

    // ✅ WhatsApp redirect
    if (link === "whatsapp") {
      const phoneNumber = "180020002600";
      const message = encodeURIComponent("Hello, I need guidance.");

      window.open(
        `https://wa.me/${phoneNumber}?text=${message}`,
        "_blank"
      );
      return;
    }

    // ✅ Internal route
    // if (link.startsWith("/")) {
    //   navigate(link);
    //   return;
    // }

    // ✅ Already valid external protocols
    // if (
    //   link.startsWith("http://") ||
    //   link.startsWith("https://") ||
    //   link.startsWith("tel:") ||
    //   link.startsWith("mailto:")
    // ) {
    //   window.open(link, "_blank", "noopener,noreferrer");
    //   return;
    // }

    // ✅ www.abc.com OR abc.com → normalize to https://
    // const domainRegex = /^(www\.)?[a-z0-9-]+\.[a-z]{2,}$/i;

    // if (domainRegex.test(link)) {
    //   const normalizedUrl = `https://${link}`;
    //   window.open(normalizedUrl, "_blank", "noopener,noreferrer");
    //   return;
    // }

    // console.warn("❌ Invalid link:", link);
  };

  const isTallContent = contentHeight > 550;

  const imageStyles = {
    width: "100%",
    maxWidth: "100%",
    height: isTallContent ? "100%" : "auto",
    maxHeight: isTallContent ? 580 : 420,
    objectFit: isTallContent ? "contain" : "contain",
    borderRadius: 2,
  };

  const hasRealContent = (html) => {
    if (!html) return false;

    const text = html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();

    return text.length > 0;
  };

  // ✅ CONTENT STYLING (KEY PART)
  const contentStyles = {
    fontSize: { xs: 14, sm: 15, md: 16 },
    lineHeight: 1.625,
    textAlign: "justify",

    "& p": {
      marginBlockStart: '0px !important',
      marginBottom: "16px",
    },

    "& p:last-of-type": {
      marginBottom: "0px",
    },

    // ✅ TARGET KEY-VALUE LINES (Language, Currency...)
    "& p:has(strong)": {
      position: "relative",
      paddingLeft: "20px",
      marginBottom: "8px",
      fontWeight: 500,
    },

    // ✅ ADD ICON BEFORE THEM
    "& p:has(strong)::before": {
      content: '"\\f101"',
      fontFamily: "FontAwesome",
      position: "absolute",
      left: 0,
      top: "2px",
      color: "#fd0000",
      fontSize: "14px",
    },

    // ✅ REMOVE DEFAULT BULLETS
    "& ul": {
      listStyle: "none",
      paddingLeft: "0px",
      marginBottom: "12px",
    },

    // ✅ CUSTOM LIST ITEM
    "& li": {
      position: "relative",
      paddingLeft: "28px",
      marginBottom: "4px",
      lineHeight: 1.7,
    },

    // ✅ FIRST ITEM
    "& li:first-of-type": {
      marginTop: "-10px",
    },

    // ✅ LAST ITEM
    "& li:last-of-type": {
      marginBottom: "15px",
    },

    // ✅ ADD ARROW ICON
    "& li::before": {
      content: '"\\f101"',
      fontFamily: "FontAwesome",
      position: "absolute",
      left: '5px',
      top: "2px",
      fontSize: "14px",
      color: "#fd0000",
    },

    "& h1, & h2, & h3, & h4": {
      marginTop: "20px",
      marginBottom: "12px",
      fontWeight: 600,
    },

    "& strong": {
      fontWeight: 600,
    },
  };

  return (
    <Container
      sx={{
        overflow: 'hidden',
        py: { xs: 3, sm: 4, md: 6 },
        px: { xs: 3, sm: 4, md: 6 },
        paddingLeft: { xs: "24px !important", md: "48px !important" },
        paddingRight: { xs: "24px !important", md: "48px !important" },
      }}
    >
      <Grid container spacing={2} alignItems="center">

        {/* Left side */}
        <Grid size={{ xs: 12, md: 6 }} ref={leftRef} component={motion.div}
          variants={fadeLeft}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}>
          <Box >
            {hasRealContent(titleContent) && (
              <Box
                sx={{
                  "& p": {
                    marginBlockStart: '0px !important',
                    marginBlockEnd: '0px !important',
                  },
                  lineHeight: {
                    xs: "36px",
                    sm: "48px",
                    md: "58px",
                  },
                  fontSize: {
                    xs: "28px",
                    sm: "36px",
                    md: "48px",
                  },
                  fontWeight: 700,
                  color: 'black',
                }}

                component="div"
                dangerouslySetInnerHTML={{
                  __html: getResponsiveTitle(titleContent),
                }}
              />
            )}

            {hasRealContent(subtitleContent) && (
              <Box
                component="div"
                dangerouslySetInnerHTML={{ __html: getResponsiveHtml(subtitleContent) }}
                sx={{
                  mt: 2,
                  fontSize: { xs: 14, sm: 15, md: 16 },
                  textAlign: "justify",
                  color: 'black',
                  ...contentStyles
                }}
              />
            )}

            {buttons.length > 0 && (
              <Stack
                direction={{ xs: "row", sm: "row" }}
                spacing={2}
                mt={2}
                justifyContent={{ xs: "center", md: "flex-start" }}
                alignItems="center"
              >
                {buttons.map((btn, idx) => (
                  <SmartButton
                    key={idx}
                    variant="contained"
                    startIcon={btn.icon}
                    onClick={() => handleClick(btn.link)}
                  >
                    {btn.label}
                  </SmartButton>
                ))}
              </Stack>
            )}

            {buttonsDesc && (
              <Box
                component="div"
                dangerouslySetInnerHTML={{ __html: getResponsiveHtml(buttonsDesc) }}
                sx={{
                  mt: 2,
                  fontSize: { xs: "14px", sm: "16px" },
                  textAlign: { xs: "center", md: "justify" },
                  color: 'black',
                }}
              />
            )}

            {points.length > 0 && (
              <Stack spacing={1.5} mt={4}>
                {points.map((point, idx) => (
                  <Stack
                    key={idx}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    justifyContent={{ xs: "center", md: "flex-start" }}
                  >
                    <Box
                      component="img"
                      src={checkCircleImage}
                      alt="check icon"
                      sx={{ width: 20, height: 20 }}
                    />
                    <Typography
                      sx={{
                        fontSize: { xs: 13, sm: 15, md: 16 },
                        fontWeight: 500,
                        color: "#000000",
                      }}
                    >
                      {point}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        </Grid>

        {/* Right side */}
        <Grid
          size={{ xs: 12, md: 6 }}
          position="relative"
          justifyContent="center"
          display="flex"
          component={motion.div}
          variants={fadeRight}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <Box
            sx={{ width: "100%", maxWidth: { xs: "100%", sm: "80%", md: "100%" }, display: "flex", justifyContent: "center" }}
          >
            {heroImage && heroImageType === "image" && (
              <Box
                component="img"
                src={heroImage}
                alt="Hero Visual"
                sx={imageStyles}
              />
            )}

            {heroImage && heroImageType === "video" && (
              <VideoPlayer
                src={heroImage}
                width="100%"
                sx={imageStyles}
                iconSize={{ xs: 40, sm: 50, md: 60 }}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </Container >
  );
};

export default HeroSection;
